<script setup lang="ts">
const props = withDefaults(defineProps<{
  overlays: IOHeroContentOverlays
  contentModel?: string
}>(), {
  overlays: () => ({ transition: { mode: 'out-in' }, overlays: [] }),
  contentModel: 'page'
})

const w = computed(() =>
  typeof window !== 'undefined' ? window.innerWidth : 1920
)

// ------------ helpers ------------
function clamp01(n: number) { return Math.max(0, Math.min(1, n)) }

function parseRgba(str: string): { r:number,g:number,b:number,a:number } | null {
  const m = str.match(/rgba?\(([^)]+)\)/i)
  if (!m) return null
  const p = m[1].split(',').map(v => v.trim())
  const [r,g,b] = p.slice(0,3).map(Number)
  const a = p[3] !== undefined ? Number(p[3]) : 1
  return { r, g, b, a: clamp01(a) }
}

function hexToRgba(hex: string): { r:number,g:number,b:number,a:number } | null {
  if (!/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(hex)) return null
  let h = hex.slice(1)
  if (h.length === 3 || h.length === 4) h = h.split('').map(c => c + c).join('')
  const r = parseInt(h.slice(0,2),16)
  const g = parseInt(h.slice(2,4),16)
  const b = parseInt(h.slice(4,6),16)
  const a = h.length === 8 ? parseInt(h.slice(6,8),16)/255 : 1
  return { r, g, b, a: clamp01(a) }
}

function toRgbaString({ r,g,b,a }:{r:number,g:number,b:number,a:number}) {
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${+a.toFixed(4)})`
}

function applyMultiplier(color: string, mult = 1): string {
  const rgba = parseRgba(color) || hexToRgba(color)
  if (!rgba) return color
  return toRgbaString({ ...rgba, a: clamp01(rgba.a * clamp01(mult)) })
}

function normDir(d?: string | number): string {
  if (typeof d === 'number') return `${d}deg`
  return d || 'to bottom'
}
function normAt(at?: string | number) {
  if (at === undefined) return undefined
  return typeof at === 'number' ? `${at}%` : at
}

function pickStyles(overlay: IOHeroContentOverlays['overlays'][number], width: number) {
  const base = overlay.styles || {}
  const bps = overlay.breakpoints || []
  const active = bps
    .filter(bp => (bp.enabled ?? true))
    .filter(bp => {
      const from = bp.from ?? 0
      const until = bp.until === -1 || bp.until === undefined ? Infinity : bp.until
      return width >= from && width < until
    })
    .sort((a, b) => (a.from ?? 0) - (b.from ?? 0))
    .pop()
  return active?.styles ? { ...base, ...active.styles } : base
}

function gradientCss(overlay: IOHeroContentOverlays['overlays'][number], width: number): string {
  const s = pickStyles(overlay, width)
  const kind = s.kind || 'linear'
  const mult = s.opacity ?? 1

  const stops = (s.stops || []).map(st => {
    const c = applyMultiplier(st.color, mult)
    const at = normAt(st.at)
    return at ? `${c} ${at}` : c
  })

  if (stops.length < 2) return 'none'

  if (kind === 'radial') return `radial-gradient(${stops.join(', ')})`
  if (kind === 'conic')  return `conic-gradient(${stops.join(', ')})`
  return `linear-gradient(${normDir(s.direction)}, ${stops.join(', ')})`
}
</script>

<template>
  <div class="layer overlay absolute inset-0 z-20 pointer-events-none">
    <transition name="fade" mode="out-in">
      <div v-if="props.contentModel === 'page' && overlays.overlays.length" key="page">
        <div v-for="(overlay, i) in overlays.overlays"
             :key="i"
             class="absolute inset-0"
             :style="{
               zIndex: i,
               backgroundImage: gradientCss(overlay, w),
               mixBlendMode: overlay.styles?.blendMode || 'normal'
             }" />
      </div>

      <div v-else key="empty" />
    </transition>
  </div>
</template>